import { type InputHTMLAttributes, useEffect } from "react"

import { useMask } from "@react-input/mask"
import {
  type FieldPath,
  type FieldPathValue,
  type FieldValues,
  useFormContext,
} from "react-hook-form"

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"

import type { CountryEnum } from "@boundless/bl-api-client/customer/models"
import type { MaskOptions } from "@react-input/mask/types"

const postalCodeMaskMapping: Record<keyof typeof CountryEnum, string> = {
  US: "#####",
  CA: "@#@ #@#",
  GB: "@%%%%??",
  AU: "####",
}

const useMaskOptions: Partial<MaskOptions> = {
  replacement: {
    // @ - Alphabetic
    "@": /[A-Z]/,
    // # - Numeric
    "#": /[0-9]/,
    // % - Alphanumeric
    "%": /[A-Z0-9]/,
    // ? - Optional Alphanumeric
    "?": /[A-Z0-9]?/,
  },
  track: ({ data, inputType }) =>
    inputType === "insert" ? data.toUpperCase() : data,
}

export type FormInputZipCodeProps<T extends FieldValues = FieldValues> = {
  name: FieldPath<T>
  countryField: FieldPath<T>
} & Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "name" | "value" | "onChange" | "onBlur" | "ref" | "defaultValue"
>

export function FormInputZipCode<T extends FieldValues = FieldValues>({
  name,
  countryField,
  className,
  ...props
}: FormInputZipCodeProps<T>) {
  const form = useFormContext<T>()
  const country = form.watch(countryField)

  // Reset the zip code when the country changes
  useEffect(() => {
    const countryState = form.getFieldState(countryField)

    if (countryState.isDirty) {
      // Country different from its default value; clear the zip code
      form.setValue(name, "" as FieldPathValue<T, typeof name>)
    } else {
      // Country is the default value; reset the zip code to its default value
      form.resetField(name)
    }
  }, [country])

  const postalCodeMask = postalCodeMaskMapping[country]
  const inputRef = useMask({
    mask: postalCodeMask,
    ...useMaskOptions,
  })

  const label = country === "US" ? "ZIP Code" : "Postal Code"

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem className="w-full">
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Input
              placeholder={label}
              {...field}
              {...props}
              className={className}
              ref={inputRef}
            />
          </FormControl>
          <FormMessage className="form-error" />
        </FormItem>
      )}
    />
  )
}
